import type { AxiosError } from 'axios';

type PrimitiveType = number | string | boolean;
type QueryObject = Record<string, PrimitiveType | PrimitiveType[] | undefined>;

export const getErrorMessage = (error: AxiosError): string => {
  if (error.response) {
    const axiosError = error as AxiosError<{ errors: [{ detail: string }]; message?: string }>;
    const errorMessage =
      axiosError.response?.data?.errors?.[0]?.detail ||
      axiosError.response?.data?.message ||
      'Something went wrong. Please try again later.';
    return errorMessage;
  } else {
    // Handle non-Axios errors
    return 'An unexpected error occurred. Please try again later.';
  }
};

const stringifyArray = (key: string, array: PrimitiveType[]): string =>
  array
    .map((arrayValue) => {
      const value = typeof arrayValue === 'boolean' ? Number(arrayValue) : arrayValue;
      return `${encodeURIComponent(key)}[]=${encodeURIComponent(value)}`;
    })
    .join('&');

const stringifyPrimitive = (key: string, value: PrimitiveType): string =>
  `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;

export const buildQueryString = (queryObject: QueryObject): string =>
  Object.entries(queryObject)
    .filter(([, value]) => typeof value !== 'undefined')
    .map(([key, value]) =>
      // we filtered out 'undefined' values earlier
      // but we still have to tell TypeScript
      // that 'value' is a PrimitiveType only
      Array.isArray(value)
        ? stringifyArray(key, value)
        : stringifyPrimitive(key, value as PrimitiveType)
    )
    .join('&');

export const safeJson = (value: unknown): unknown => {
  try {
    if (value == null) {
      return value;
    }

    // Axios config.data can be a string already (JSON string)
    if (typeof value === 'string') {
      // keep it but cap size
      return value.slice(0, 10_000);
    }

    // FormData / Blob / ArrayBuffer etc.
    if (typeof FormData !== 'undefined' && value instanceof FormData) {
      return '[FormData]';
    }
    if (typeof Blob !== 'undefined' && value instanceof Blob) {
      return `[Blob size=${value.size}]`;
    }

    return JSON.parse(JSON.stringify(value));
  } catch {
    return '[Unserializable]';
  }
};
